import { useController } from '@aha-app/mvc';
import WhiteboardController from '../../controllers/WhiteboardController';
import { useEffect, useRef, useState } from 'react';

const useLatestRef = <T>(value: T) => {
  const ref = useRef(value);
  ref.current = value;
  return ref;
};

/**
 * A hook that gives you the currently selected object, only if it matches the predicate. Use
 * it in components that display information about specific types of objects when selected on
 * the canvas.
 *
 * @param {function} options.predicate - Function that returns true if the object is of the desired type (pass a function straight from guards.ts)
 * @param {function} [options.onChange] - Optional callback that fires when the selected object changes
 * @returns {T | undefined} The currently selected object that matches the predicate, or undefined if none selected
 *
 * @example
 * const selectedFrame = useSelectedObject({
 *   predicate: isFrame,
 *   onChange: (obj) => console.log(obj ? 'Frame selected' : 'No frame selected', obj)
 * });
 */
const useSelectedObject = <T extends fabric.Object>(
  callbacks: {
    predicate?: (obj: fabric.Object) => obj is T;
    onChange?: (obj: T | undefined) => void;
  } = {}
): T | undefined => {
  const controller = useController(WhiteboardController);
  const [selectedObject, setSelectedObject] = useState<T | undefined>();

  const callbacksRef = useLatestRef(callbacks);
  const selectedObjectRef = useLatestRef(selectedObject);

  useEffect(() => {
    const onUpdate = () => {
      const { predicate = (obj): obj is T => true, onChange } =
        callbacksRef.current;
      const currentObj = controller.canvas.getActiveObject();
      const isValidObject = currentObj && predicate(currentObj);
      const selected = isValidObject ? currentObj : undefined;
      if (onChange && selected !== selectedObjectRef.current) {
        onChange?.(selected);
      }
      setSelectedObject(selected);
    };

    // initial update
    onUpdate();

    const events = [
      'object:changed',
      'selection:created',
      'selection:updated',
      'selection:cleared',
    ];
    events.forEach(event => controller.canvas.on(event, onUpdate));
    return () => {
      events.forEach(event => controller.canvas.off(event, onUpdate));
    };
  }, [controller.canvas]);

  return selectedObject;
};

export default useSelectedObject;
